import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import { getTrackerHistory } from "../../services/trackerHistoryService";
import "./TrackerHistoryModal.css";

const PAGE_SIZE = 20;

function formatDate(dateValue) {
    if (!dateValue) {
        return "-";
    }

    const normalizedDate = String(dateValue).replace(" ", "T");
    const date = new Date(normalizedDate);

    if (Number.isNaN(date.getTime())) {
        return dateValue;
    }

    return new Intl.DateTimeFormat("es-PE", {
        dateStyle: "medium",
        timeStyle: "medium",
    }).format(date);
}

function tiempoTranscurrido(fechaInicio, fechaFin) {
    const inicio = new Date(fechaInicio.replace(" ", "T"));
    const fin = new Date(fechaFin.replace(" ", "T"));

    let segundosTotales = Math.floor((fin - inicio) / 1000);

    if (segundosTotales < 0) {
        return "Fecha inválida";
    }

    const horas = Math.floor(segundosTotales / 3600);
    const minutos = Math.floor((segundosTotales % 3600) / 60);
    const segundos = segundosTotales % 60;

    const partes = [];

    if (horas > 0) {
        partes.push(`${horas}h`);
    }

    if (minutos > 0) {
        partes.push(`${minutos}min`);
    }

    if (segundos > 0 || partes.length === 0) {
        partes.push(`${segundos}seg`);
    }

    return partes.join(" ");
}

export default function TrackerHistoryModal({
    open,
    token,
    userName,
    onClose,
}) {
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const scrollContainerRef = useRef(null);
    const observerRef = useRef(null);
    const abortControllerRef = useRef(null);

    const loadHistory = useCallback(
        async (pageToLoad, replace = false) => {
            if (!token || loading) {
                return;
            }

            setLoading(true);
            setError("");

            abortControllerRef.current?.abort();

            const controller = new AbortController();
            abortControllerRef.current = controller;

            try {
                const response = await getTrackerHistory({
                    token,
                    page: pageToLoad,
                    limit: PAGE_SIZE,
                    signal: controller.signal,
                });

                setItems((currentItems) => {
                    if (replace) {
                        return response.data;
                    }

                    /*
                     * Evita duplicados cuando IntersectionObserver
                     * se ejecuta más de una vez.
                     */
                    const existingKeys = new Set(
                        currentItems.map((item) =>
                            item.id
                                ? `id-${item.id}`
                                : `${item.link}-${item.created_at}`
                        )
                    );

                    const newItems = response.data.filter((item) => {
                        const key = item.id
                            ? `id-${item.id}`
                            : `${item.link}-${item.created_at}`;

                        return !existingKeys.has(key);
                    });

                    return [...currentItems, ...newItems];
                });

                setPage(pageToLoad);
                setHasMore(response.pagination.has_more);
            } catch (requestError) {
                if (requestError.name !== "AbortError") {
                    setError(
                        requestError.message ??
                            "No se pudo cargar el historial"
                    );
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        },
        [token, loading]
    );

    /*
     * Reinicia el historial cuando se abre el modal
     * o cambia el token.
     */
    useEffect(() => {
        if (!open || !token) {
            return undefined;
        }

        setItems([]);
        setPage(1);
        setHasMore(true);
        setError("");
        setLoading(false);

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const loadInitialHistory = async () => {
            setLoading(true);

            try {
                const response = await getTrackerHistory({
                    token,
                    page: 1,
                    limit: PAGE_SIZE,
                    signal: controller.signal,
                });

                setItems(response.data);
                setPage(1);
                setHasMore(response.pagination.has_more);
            } catch (requestError) {
                if (requestError.name !== "AbortError") {
                    setError(
                        requestError.message ??
                            "No se pudo cargar el historial"
                    );
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        loadInitialHistory();

        return () => {
            controller.abort();
        };
    }, [open, token]);

    /*
     * Esta función se asigna al último elemento.
     * Cuando el elemento se hace visible, carga la siguiente página.
     */
    const lastItemRef = useCallback(
        (node) => {
            observerRef.current?.disconnect();

            if (
                !node ||
                loading ||
                !hasMore ||
                !scrollContainerRef.current
            ) {
                return;
            }

            observerRef.current = new IntersectionObserver(
                (entries) => {
                    const firstEntry = entries[0];

                    if (
                        firstEntry.isIntersecting &&
                        !loading &&
                        hasMore
                    ) {
                        loadHistory(page + 1);
                    }
                },
                {
                    root: scrollContainerRef.current,
                    rootMargin: "150px",
                    threshold: 0.1,
                }
            );

            observerRef.current.observe(node);
        },
        [loading, hasMore, loadHistory, page]
    );

    /*
     * Cierra el modal al presionar Escape.
     */
    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "hidden";

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "";
            observerRef.current?.disconnect();
            abortControllerRef.current?.abort();
        };
    }, [open, onClose]);

    if (!open) {
        return null;
    }

    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            className="tracker-history-overlay"
            onMouseDown={handleOverlayClick}
            role="presentation"
        >
            <section
                className="tracker-history-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tracker-history-title"
            >
                <header className="tracker-history-header">
                    <div>
                        <h2 id="tracker-history-title">
                            Historial de navegación
                        </h2>

                        <p>
                            {userName || "Usuario"}
                        </p>
                    </div>

                    <button
                        type="button"
                        className="tracker-history-close"
                        onClick={onClose}
                        aria-label="Cerrar modal"
                    >
                        ×
                    </button>
                </header>

                <div className="tracker-history-token">
                    <span>Token:</span>
                    <strong>{token}</strong>
                </div>

                <div className="tracker-history-columns">
                    <span>Enlace</span>
                    <span>Fecha y hora</span>
                </div>

                <div
                    ref={scrollContainerRef}
                    className="tracker-history-content"
                >
                    {items.map((item, index) => {
                        const isLastItem =
                            index === items.length - 1;

                        const key = item.id
                            ? `history-${item.id}`
                            : `history-${item.link}-${item.created_at}-${index}`;

                        return (
                            <article
                                ref={isLastItem ? lastItemRef : null}
                                key={key}
                                className="tracker-history-item"
                            >
                                <div className="tracker-history-link">
                                    <a
                                        href={item.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={item.link}
                                    >
                                        {item.link || "Sin enlace"}
                                    </a>
                                </div>

                                <time
                                    dateTime={String(
                                        item.created_at
                                    ).replace(" ", "T")}
                                >
                                    {formatDate(item.created_at)}
                                </time>
                                {
                                    item.finished_at !== null ? 
                                    <span className="time-connected">Conectado: {tiempoTranscurrido(item.created_at, item.finished_at)}</span>
                                    :
                                    null
                                }
                            </article>
                        );
                    })}

                    {!loading &&
                        items.length === 0 &&
                        !error && (
                            <div className="tracker-history-empty">
                                No existen registros para este usuario.
                            </div>
                        )}

                    {error && (
                        <div className="tracker-history-error">
                            <p>{error}</p>

                            <button
                                type="button"
                                onClick={() =>
                                    loadHistory(
                                        items.length === 0
                                            ? 1
                                            : page + 1,
                                        items.length === 0
                                    )
                                }
                            >
                                Reintentar
                            </button>
                        </div>
                    )}

                    {loading && (
                        <div className="tracker-history-loading">
                            <span className="tracker-history-spinner" />
                            Cargando historial...
                        </div>
                    )}

                    {!loading &&
                        !hasMore &&
                        items.length > 0 && (
                            <div className="tracker-history-end">
                                No hay más registros.
                            </div>
                        )}
                </div>
            </section>
        </div>
    );
}